import { useEffect, useRef, useState } from 'react';

import learnSlide from '../../../img/renaissance-new/HeroSection/Learn.webp';
import liveClassSlide from '../../../img/renaissance-new/HeroSection/Live Class.webp';
import mentorSlide from '../../../img/renaissance-new/HeroSection/Mentor.webp';
import mockAssessmentsSlide from '../../../img/renaissance-new/HeroSection/Mock Assesssments.webp';
import videoPlayerSlide from '../../../img/renaissance-new/HeroSection/Video Player.webp';

const LEARN = 0;
const LIVE = 1;
const MENTOR = 2;
const MOCK = 3;
const VIDEO = 4;

const SLIDES = [
  { src: learnSlide },
  { src: liveClassSlide },
  { src: mentorSlide },
  { src: mockAssessmentsSlide },
  { src: videoPlayerSlide },
];

// Every slide shares the same screenshot canvas, so a click point defined as a
// fraction of the frame lands on the same UI element regardless of which
// slide is currently visible. Each step describes: the slide the cursor acts
// on, where it clicks, the highlight box around that target, and which slide
// that click reveals next.
const SIDEBAR_BOX = { w: 0.17, h: 0.036 };
const STEPS = [
  {
    slide: LEARN,
    clickTo: VIDEO,
    point: { x: 0.3435, y: 0.1995 },
    box: { w: 0.152, h: 0.047 },
  },
  {
    slide: VIDEO,
    clickTo: LEARN,
    point: { x: 0.2875, y: 0.1605 },
    box: { w: 0.048, h: 0.026 },
  },
  { slide: LEARN, clickTo: LIVE, point: { x: 0.136, y: 0.2335 }, box: SIDEBAR_BOX },
  { slide: LIVE, clickTo: MENTOR, point: { x: 0.136, y: 0.2905 }, box: SIDEBAR_BOX },
  { slide: MENTOR, clickTo: MOCK, point: { x: 0.136, y: 0.5752 }, box: SIDEBAR_BOX },
  { slide: MOCK, clickTo: LEARN, point: { x: 0.136, y: 0.1755 }, box: SIDEBAR_BOX },
];

const HOLD_MS = 1500;
const MOVE_MS = 900;
const HOVER_MS = 550;
const CLICK_MS = 300;
const TRANSITION_MS = 700;

const CursorIcon = ({ clicking }) => (
  <svg
    width="26"
    height="26"
    viewBox="0 0 26 26"
    style={{
      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.35))',
      transform: clicking ? 'scale(0.85)' : 'scale(1)',
      transition: 'transform 150ms ease-out',
    }}
  >
    <path
      d="M3 1.5 L3 19.5 L7.6 15.4 L10.6 22.5 L13.6 21.2 L10.7 14.2 L17 14 Z"
      fill="white"
      stroke="#1e1b4b"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
  </svg>
);

const HeroImage = () => {
  const [activeIndex, setActiveIndex] = useState(LEARN);
  const [cursorPos, setCursorPos] = useState({ x: 0.5, y: 0.92 });
  const [phase, setPhase] = useState('idle'); // idle | moving | hover | click
  const timersRef = useRef([]);

  useEffect(() => {
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return undefined;

    let cancelled = false;
    const schedule = (fn, delay) => {
      const id = setTimeout(() => {
        if (!cancelled) fn();
      }, delay);
      timersRef.current.push(id);
    };

    const runStep = (stepIndex) => {
      const step = STEPS[stepIndex % STEPS.length];

      schedule(() => {
        setPhase('moving');
        setCursorPos({ x: step.point.x, y: step.point.y });

        schedule(() => {
          setPhase('hover');

          schedule(() => {
            setPhase('click');

            schedule(() => {
              setActiveIndex(step.clickTo);
              setPhase('idle');

              schedule(() => runStep(stepIndex + 1), TRANSITION_MS);
            }, CLICK_MS);
          }, HOVER_MS);
        }, MOVE_MS);
      }, HOLD_MS);
    };

    runStep(0);

    return () => {
      cancelled = true;
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
  }, []);

  return (
    <div className="relative w-full max-w-[1120px] aspect-[1120/638] overflow-hidden rounded-xl sm:rounded-none drop-shadow-[0px_12px_36px_rgba(0,0,0,0.25)] sm:drop-shadow-none">
      {SLIDES.map((slide, index) => (
        <div
          key={slide.src}
          className="absolute inset-0 w-full h-full transition-opacity duration-700 ease-in-out"
          style={{ opacity: index === activeIndex ? 1 : 0, willChange: 'opacity' }}
        >
          <img
            src={slide.src}
            alt="Renaissance dashboard preview"
            className="w-full h-full object-cover"
          />
        </div>
      ))}

      {phase === 'click' && (
        <span
          className="absolute rounded-full bg-white pointer-events-none"
          style={{
            left: `${cursorPos.x * 100}%`,
            top: `${cursorPos.y * 100}%`,
            width: 28,
            height: 28,
            marginLeft: -14,
            marginTop: -14,
            opacity: 0.55,
            animation: `ppaCursorRipple ${CLICK_MS + 150}ms ease-out forwards`,
          }}
        />
      )}

      <div
        className="absolute z-20 pointer-events-none"
        style={{
          left: `${cursorPos.x * 100}%`,
          top: `${cursorPos.y * 100}%`,
          transitionProperty: 'left, top',
          transitionDuration: `${MOVE_MS}ms`,
          transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <CursorIcon clicking={phase === 'click'} />
      </div>

      <style>{`
        @keyframes ppaCursorRipple {
          0% { transform: scale(0.5); opacity: 0.55; }
          100% { transform: scale(2); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default HeroImage;
